import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MatchAlert — bottom-right corner toast (not a fullscreen takeover).
 * Polls notifications for unread `match` events.
 */
export default function MatchAlert() {
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      setMatch(null);
      seenIdsRef.current = new Set();
      initializedRef.current = false;
      return;
    }
    let active = true;
    let timer = null;
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications");
        const matches = (data.items || []).filter(n => n.type === "match");
        if (!initializedRef.current) {
          matches.forEach(m => seenIdsRef.current.add(m.id));
          initializedRef.current = true;
        } else {
          const fresh = matches.find(m => !seenIdsRef.current.has(m.id) && !m.read);
          if (fresh && active) {
            setMatch(fresh);
            seenIdsRef.current.add(fresh.id);
          }
          matches.forEach(m => seenIdsRef.current.add(m.id));
        }
      } catch (e) { /* ignore */ }
      if (active) timer = setTimeout(poll, 25000);
    };
    poll();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [user]);

  const dismiss = async () => {
    if (match) {
      try { await api.post(`/notifications/${match.id}/read`); } catch (e) { /* ignore */ }
    }
    setMatch(null);
  };

  if (!match) return null;

  return (
    <div
      className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-[60] max-w-sm w-[calc(100vw-2rem)] md:w-96 slide-in-right"
      data-testid="match-alert"
    >
      <div className="vyrti-card-raised relative overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-[#0654BA] via-[#F5AF02] to-[#E53238]" />

        <button
          onClick={dismiss}
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
          data-testid="match-alert-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#E0EDFA] text-[#0654BA] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-widest uppercase font-bold text-[#F5AF02] mb-1">Match found</p>
              <p className="font-display font-bold text-slate-900 text-base leading-tight line-clamp-2">{match.title}</p>
              <p className="text-sm text-slate-600 leading-snug mt-1.5 line-clamp-2">{match.body}</p>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            {match.link && (
              <Link
                to={match.link}
                onClick={dismiss}
                className="btn-block btn-accent !text-sm !py-2 flex-1"
                data-testid="match-alert-cta"
              >
                See the match <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
            <button
              onClick={dismiss}
              className="btn-block btn-ghost !text-sm !py-2"
              data-testid="match-alert-later"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
